import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { ADS_ENABLED, REWARDED_AD_COINS } from '@/config/ads';
import { useThemePalette } from '@/config/theme';
import {
  useAchievements,
  useAdRewardState,
  useBuyStreakFreeze,
  useDailyCheckin,
  useDailyMissions,
  useMyRecentPrize,
  useStreakDays,
  useWallet,
  useWatchAdForCoins,
} from '@/features/games/rewards';
import { useAuth } from '@/lib/auth/AuthProvider';
import { errorKey } from '@/lib/utils/errors';
import type { DailyMission } from '@/lib/data/provider';
import { dayKey, formatNumber, localizeDigits } from '@/lib/i18n/format';
import { localizeRound } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';

type IoniconName = keyof typeof Ionicons.glyphMap;

const FREEZE_COST = 150;

const MISSION_ICONS: Record<string, IoniconName> = {
  open_app: 'log-in',
  make_prediction: 'create',
  play_challenge: 'flash',
  submit_acca: 'layers',
  survivor_pick: 'shield-half',
  spin_today: 'sync-circle',
  follow_player: 'heart',
};

/**
 * The last 14 calendar days as yyyy-mm-dd in the APP timezone (Africa/Cairo, via
 * dayKey), built once. Must use the same timezone the server keys check-ins on,
 * or the today-dot lands on the wrong number near midnight.
 */
function useRecentDays(): string[] {
  return useState(() => {
    const out: string[] = [];
    const now = new Date();
    for (let i = 13; i >= 0; i -= 1) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      out.push(dayKey(d));
    }
    return out;
  })[0];
}

function StreakStrip({ activeDays }: { activeDays: Set<string> }) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const days = useRecentDays();
  const today = days[days.length - 1];
  return (
    <View className="flex-row justify-between">
      {days.map((day) => {
        const active = activeDays.has(day);
        const isToday = day === today;
        const dayNum = Number(day.slice(-2));
        return (
          <View key={day} className="items-center gap-1" style={{ flex: 1 }}>
            <View
              className="h-7 w-7 items-center justify-center rounded-full"
              style={{
                backgroundColor: active ? palette.voltBright : palette.inset,
                borderWidth: isToday ? 1.5 : 0,
                borderColor: palette.voltBright,
              }}
            >
              {active ? <Ionicons name="checkmark" size={14} color="#0B1A0E" /> : null}
            </View>
            <AppText variant="micro" tabular className="text-muted" style={{ fontSize: 9 }}>
              {localizeDigits(String(dayNum), arabicNumerals)}
            </AppText>
          </View>
        );
      })}
    </View>
  );
}

function MissionRow({ mission }: { mission: DailyMission }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const icon = MISSION_ICONS[mission.key] ?? 'flash';
  const pct = Math.min(1, mission.progress / Math.max(1, mission.target));
  return (
    <View className="flex-row items-center gap-3 px-4 py-3">
      <View
        className="h-10 w-10 items-center justify-center rounded-full"
        style={{ backgroundColor: mission.completed ? palette.voltGlassStrong : palette.inset }}
      >
        <Ionicons name={icon} size={18} color={mission.completed ? palette.win : palette.voltBright} />
      </View>
      <View className="flex-1 gap-1.5">
        <View className="flex-row items-center justify-between">
          <AppText variant="caption" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
            {t(`rewards.mission.${mission.key}`)}
          </AppText>
          <View className="flex-row items-center gap-1">
            <Ionicons name="server" size={12} color={palette.voltBright} />
            <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
              {`+${formatNumber(mission.reward_coins, numeralOptions)}`}
            </AppText>
          </View>
        </View>
        <View className="h-1.5 overflow-hidden rounded-full bg-inset">
          <View
            className="h-full rounded-full"
            style={{ width: `${pct * 100}%`, backgroundColor: mission.completed ? palette.win : palette.voltBright }}
          />
        </View>
      </View>
      <Ionicons
        name={mission.completed ? 'checkmark-circle' : 'ellipse-outline'}
        size={18}
        color={mission.completed ? palette.win : palette.inactive}
      />
    </View>
  );
}

const ACH_ICONS: Record<string, IoniconName> = {
  first_squad: 'shirt',
  first_captain: 'star',
  first_challenge: 'flash',
  first_prediction: 'help-circle',
  predict_correct: 'checkmark-done',
  first_acca: 'layers',
  acca_jackpot: 'trophy',
  survivor_join: 'shield-half',
  survivor_run: 'shield-checkmark',
  spin_master: 'sync-circle',
  follow_five: 'heart',
  streak_seven: 'flame',
  score_first: 'football',
  score_exact: 'locate',
  prize_ticket: 'ticket',
  prize_winner: 'gift',
  coin_collector: 'cash',
  friend_league: 'people',
};

function StatPill({ icon, value, label, color }: { icon: IoniconName; value: string; label: string; color: string }) {
  return (
    <View className="flex-1 items-center gap-1 py-1">
      <Ionicons name={icon} size={22} color={color} />
      <AppText variant="title" weight="bold" tabular className="text-ink">
        {value}
      </AppText>
      <AppText variant="micro" className="uppercase tracking-wide text-muted">
        {label}
      </AppText>
    </View>
  );
}

function AchievementRow({ ach }: { ach: { key: string; unlocked_at: string | null } }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const unlocked = ach.unlocked_at != null;
  const icon = ACH_ICONS[ach.key] ?? 'ribbon';
  return (
    <View className="flex-row items-center gap-3 px-4 py-3">
      {unlocked ? (
        <LinearGradient
          colors={[palette.voltGlassStrong, palette.voltGlass]}
          start={{ x: 0.2, y: 0 }}
          end={{ x: 0.8, y: 1 }}
          style={{
            height: 42,
            width: 42,
            borderRadius: 21,
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: palette.voltLine,
            shadowColor: palette.volt,
            shadowOpacity: 0.5,
            shadowRadius: 6,
            shadowOffset: { width: 0, height: 0 },
            elevation: 5,
          }}
        >
          <Ionicons name={icon} size={20} color={palette.voltBright} />
        </LinearGradient>
      ) : (
        <View
          className="h-10 w-10 items-center justify-center rounded-full border border-line"
          style={{ backgroundColor: palette.inset, opacity: 0.7 }}
        >
          <Ionicons name={icon} size={18} color={palette.inactive} />
          <View
            className="absolute h-4 w-4 items-center justify-center rounded-full"
            style={{ bottom: -1, right: -1, backgroundColor: palette.deep, borderWidth: 1, borderColor: palette.line }}
          >
            <Ionicons name="lock-closed" size={9} color={palette.inactive} />
          </View>
        </View>
      )}
      <View className="flex-1">
        <AppText
          variant="caption"
          weight="bold"
          className={unlocked ? 'text-ink' : 'text-soft'}
          numberOfLines={1}
        >
          {t(`rewards.ach.${ach.key}.title`)}
        </AppText>
        <AppText variant="micro" className="text-muted" numberOfLines={2}>
          {t(`rewards.ach.${ach.key}.desc`)}
        </AppText>
      </View>
      <Ionicons
        name={unlocked ? 'checkmark-circle' : 'lock-closed'}
        size={18}
        color={unlocked ? palette.win : palette.inactive}
      />
    </View>
  );
}

export default function RewardsScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const { userId } = useAuth();
  const router = useRouter();
  const toast = useToast();
  const wallet = useWallet();
  const achievements = useAchievements();
  const missions = useDailyMissions();
  const streakDays = useStreakDays();
  const recentPrize = useMyRecentPrize();
  const checkin = useDailyCheckin();
  const watchAd = useWatchAdForCoins();
  const adState = useAdRewardState();
  const buyFreeze = useBuyStreakFreeze();

  const onBuyFreeze = () => {
    buyFreeze.mutate(undefined, {
      onSuccess: () => toast.show({ message: t('rewards.freezeBought'), tone: 'success' }),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  const onWatchAd = () => {
    watchAd.mutate(undefined, {
      onSuccess: (r) =>
        toast.show({
          message: t('rewards.adReward', { coins: formatNumber(r.reward, numeralOptions) }),
          tone: 'success',
        }),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  const w = wallet.data;
  const unlockedCount = (achievements.data ?? []).filter((a) => a.unlocked_at != null).length;
  const activeDays = useMemo(() => new Set(streakDays.data ?? []), [streakDays.data]);

  const onCheckin = () => {
    checkin.mutate(undefined, {
      onSuccess: (res) => {
        if (res.already_checked_in) {
          toast.show({ message: t('rewards.alreadyToday'), tone: 'info' });
        } else {
          toast.show({
            message: t('rewards.claimed', {
              coins: formatNumber(res.reward, numeralOptions),
              streak: formatNumber(res.streak, numeralOptions),
            }),
            tone: 'success',
          });
        }
      },
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  return (
    <Screen>
      <BackHeader title={t('rewards.title')} />
      <ScrollView contentContainerStyle={{ paddingBottom: 32, gap: 12 }}>
        {!userId ? (
          <EmptyState icon="gift-outline" title={t('rewards.signInTitle')} body={t('predictions.signInToPlay')} />
        ) : (
          <>
            {/* Wallet header */}
            <Card className="mx-4 mt-1 px-4 py-4">
              {wallet.isPending ? (
                <Skeleton className="h-16 w-full" />
              ) : (
                <View className="flex-row items-center">
                  <StatPill
                    icon="flame"
                    value={formatNumber(w?.streak ?? 0, numeralOptions)}
                    label={t('rewards.streak')}
                    color={palette.captain}
                  />
                  <View className="h-12 w-px bg-line" />
                  <StatPill
                    icon="server"
                    value={formatNumber(w?.coins ?? 0, numeralOptions)}
                    label={t('rewards.coins')}
                    color={palette.voltBright}
                  />
                  <View className="h-12 w-px bg-line" />
                  <StatPill
                    icon="ribbon"
                    value={formatNumber(unlockedCount, numeralOptions)}
                    label={t('rewards.badges')}
                    color={palette.win}
                  />
                </View>
              )}
            </Card>

            {/* Weekly prize win */}
            {recentPrize.data ? (
              <View className="mx-4 mb-1 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3">
                <Ionicons name="trophy" size={20} color={palette.captain} />
                <AppText variant="micro" weight="medium" className="flex-1 leading-5 text-soft">
                  {t('rewards.prizeWon', {
                    rank: formatNumber(recentPrize.data.rank, numeralOptions),
                    coins: formatNumber(recentPrize.data.coins, numeralOptions),
                    round: recentPrize.data.round_label
                      ? localizeRound(recentPrize.data.round_label, locale)
                      : '',
                  })}
                </AppText>
              </View>
            ) : null}

            {/* Coins store entry */}
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push('/store' as never)}
              className="mx-4 mb-1 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3.5 active:opacity-80"
            >
              <Ionicons name="bag-handle" size={20} color={palette.voltBright} />
              <View className="flex-1">
                <AppText variant="caption" weight="bold" className="text-ink">
                  {t('rewards.storeTitle')}
                </AppText>
                <AppText variant="micro" className="text-muted">
                  {t('rewards.storeHint')}
                </AppText>
              </View>
              <Ionicons name="chevron-forward" size={16} color={palette.muted} />
            </Pressable>

            {/* Watch a rewarded ad for coins - shows how many are left today and
                locks once the daily cap is hit. `remaining` defaults high until
                the state loads, so we never flash a disabled button on open. */}
            {ADS_ENABLED ? (
              (() => {
                // While the state loads we treat the ad as available (remaining
                // unknown) so the button never flashes disabled on open; the cap
                // line and lock appear once the real count arrives.
                const remaining = adState.data?.remaining;
                const exhausted = remaining !== undefined && remaining <= 0;
                return (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityState={{ disabled: exhausted || watchAd.isPending }}
                    disabled={exhausted || watchAd.isPending}
                    onPress={onWatchAd}
                    className={`mx-4 mb-1 flex-row items-center gap-3 rounded-card border border-win/30 bg-win/10 px-4 py-3.5 active:opacity-80 ${
                      exhausted ? 'opacity-50' : ''
                    }`}
                  >
                    <Ionicons
                      name={watchAd.isPending ? 'hourglass-outline' : exhausted ? 'time-outline' : 'play-circle'}
                      size={20}
                      color={palette.win}
                    />
                    <View className="flex-1">
                      <AppText variant="caption" weight="bold" className="text-ink">
                        {t('rewards.watchAdTitle')}
                      </AppText>
                      <AppText variant="micro" className="text-muted">
                        {exhausted
                          ? t('rewards.adBackTomorrow')
                          : remaining !== undefined
                            ? t('rewards.adRewardsLeft', {
                                n: formatNumber(remaining, numeralOptions),
                              })
                            : t('rewards.watchAdHint', {
                                coins: formatNumber(REWARDED_AD_COINS, numeralOptions),
                              })}
                      </AppText>
                    </View>
                    <View className="flex-row items-center gap-0.5">
                      <Ionicons name="add" size={14} color={palette.win} />
                      <AppText variant="caption" weight="bold" tabular style={{ color: palette.win }}>
                        {formatNumber(REWARDED_AD_COINS, numeralOptions)}
                      </AppText>
                    </View>
                  </Pressable>
                );
              })()
            ) : null}

            {/* Daily check-in */}
            <View className="mx-4">
              {w?.checked_in_today ? (
                <View className="flex-row items-center gap-3 rounded-card border border-line bg-surface px-4 py-3.5">
                  <Ionicons name="checkmark-circle" size={20} color={palette.win} />
                  <AppText variant="caption" weight="medium" className="flex-1 text-soft">
                    {t('rewards.comeBackTomorrow')}
                  </AppText>
                </View>
              ) : (
                <Button
                  label={t('rewards.claimDaily')}
                  variant="primary"
                  loading={checkin.isPending}
                  onPress={onCheckin}
                />
              )}
              <AppText variant="micro" className="mt-2 px-1 text-center text-muted">
                {t('rewards.checkinHint')}
              </AppText>
            </View>

            {/* Streak calendar */}
            <View className="mx-4 mt-2 flex-row items-center justify-between">
              <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-soft">
                {t('rewards.streakCalendar')}
              </AppText>
              <View className="flex-row items-center gap-1">
                <AppText variant="micro">🛡️</AppText>
                <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
                  {t('rewards.freezesLabel', { n: formatNumber(w?.streak_freezes ?? 0, numeralOptions) })}
                </AppText>
              </View>
            </View>
            <Card className="mx-4 px-3 py-3">
              {streakDays.isPending ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <StreakStrip activeDays={activeDays} />
              )}
            </Card>
            <Pressable
              accessibilityRole="button"
              disabled={buyFreeze.isPending}
              onPress={onBuyFreeze}
              className="mx-4 flex-row items-center justify-center gap-1.5 rounded-card border border-volt-line bg-volt-glass px-4 py-2.5 active:opacity-80"
            >
              <Ionicons name={buyFreeze.isPending ? 'hourglass-outline' : 'shield-half'} size={16} color={palette.voltBright} />
              <AppText variant="micro" weight="bold" className="text-volt-bright">
                {t('rewards.buyFreeze', { coins: formatNumber(FREEZE_COST, numeralOptions) })}
              </AppText>
            </Pressable>

            {/* Daily missions */}
            <AppText variant="caption" weight="bold" className="mx-4 mt-2 uppercase tracking-wide text-soft">
              {t('rewards.missions')}
            </AppText>
            {missions.isPending ? (
              <View className="gap-3 px-4">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </View>
            ) : (
              <Card className="mx-4 overflow-hidden">
                {(missions.data ?? []).map((mission, index) => (
                  <View key={mission.key} className={index > 0 ? 'border-t border-line' : ''}>
                    <MissionRow mission={mission} />
                  </View>
                ))}
              </Card>
            )}

            {/* Achievements */}
            <View className="mx-4 mt-2 flex-row items-center justify-between">
              <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-soft">
                {t('rewards.achievements')}
              </AppText>
              {!achievements.isPending && (achievements.data ?? []).length > 0 ? (
                <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
                  {t('profile.badgesCount', {
                    unlocked: formatNumber(unlockedCount, numeralOptions),
                    total: formatNumber((achievements.data ?? []).length, numeralOptions),
                  })}
                </AppText>
              ) : null}
            </View>
            {achievements.isPending ? (
              <View className="gap-3 px-4">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </View>
            ) : (
              <Card className="mx-4 overflow-hidden">
                {(achievements.data ?? []).map((ach, index) => (
                  <View key={ach.key} className={index > 0 ? 'border-t border-line' : ''}>
                    <AchievementRow ach={ach} />
                  </View>
                ))}
              </Card>
            )}
          </>
        )}
      </ScrollView>
    </Screen>
  );
}
